import { Flex, Icon, IconButton, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { cloneDeep } from "lodash";
import { useCallback } from "react";
import { FaTrash } from "react-icons/fa";
import {
  DefaultNotificationSettingsDto,
  SupplierNotificationReceiversDto,
  useUpdateOrganizationMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useAppDispatch } from "../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../common/redux/reducers/authStateReducer";
import { initSettings } from "./NotificationSettings";

type ReceiverType = "inviteReceivers" | "messageReceivers";

export const SupplierNotificationReceiverSetting = ({
  receivers,
  receiverType,
}: {
  receivers: SupplierNotificationReceiversDto;
  receiverType: ReceiverType;
}) => {
  const dispatch = useAppDispatch();
  const authState = useLoggedInWithOrgContextState();

  const [updateOrg, { isLoading }] = useUpdateOrganizationMutation();

  const deleteSetting = useCallback(async () => {
    const settings: DefaultNotificationSettingsDto =
      cloneDeep(authState.selectedOrg.defaultNotificationSettings) ?? initSettings();
    if (settings.supplierNotificationSettings) {
      settings.supplierNotificationSettings[receiverType] = {
        persons: [],
        organizationRoles: [],
      };
    }
    const response = await updateOrg({
      orgId: authState.selectedOrg.id,
      editOrganizationRequest: {
        defaultNotificationSettings: settings,
      },
    });
    if ("data" in response) {
      dispatch(updateSelectedOrg(response.data));
    }
  }, [authState.selectedOrg.defaultNotificationSettings, authState.selectedOrg.id, dispatch, receiverType, updateOrg]);

  const hasReceivers = receivers.persons.length > 0 || receivers.organizationRoles.length > 0;

  if (!hasReceivers) return null;

  return (
    <Flex flexDirection={"column"} fontSize={"sm"} backgroundColor={"gray.50"} rounded="md" p="2" my="1">
      <Flex justifyContent={"space-between"} width="full">
        <Text fontWeight={"bold"}>{t("Receivers")}</Text>
        <IconButton
          aria-label="delete setting"
          icon={<Icon as={FaTrash} />}
          size={"xs"}
          isLoading={isLoading}
          onClick={() => deleteSetting()}
        />
      </Flex>
      {receivers.persons.length > 0 && (
        <>
          <Text>{t("Individual receivers")}</Text>
          {receivers.persons.map((person) => (
            <Text key={person.id} fontWeight={"bold"}>
              {person.firstName} {person.lastName}
            </Text>
          ))}
        </>
      )}
      {receivers.organizationRoles.length > 0 && (
        <>
          <Text>{t("Organization role receivers")}</Text>
          {receivers.organizationRoles.map((role) => (
            <Text key={role} fontWeight={"bold"}>
              {role}
            </Text>
          ))}
        </>
      )}
    </Flex>
  );
};
